import { useState, type KeyboardEvent, type MouseEvent } from "react";
import {
  type ChargingStation,
  formatStationDistance,
  getStationDisplayName
} from "../../config/amapChargingDemo";
import {
  formatCarwashDistance,
  getCarwashDisplayName,
  type CarwashShop
} from "../../config/amapCarwashDemo";
import {
  formatFuelDistance,
  formatFuelPrice,
  getFuelStationDisplayName,
  type FuelStation
} from "../../config/amapFuelDemo";
import {
  formatPickupDistance,
  getPickupPointDisplayName,
  type ParcelPickupPoint
} from "../../config/amapParcelDemo";
import { TARGET_BATTERY_LEVEL, USABLE_BATTERY_KWH } from "../../config/chargingSession";
import type { TraditionalDesktopDestinationTarget } from "./TraditionalDesktopMapDestination";

export type TraditionalChargingFlowStage =
  | "selection"
  | "toCharging"
  | "arrivedCharging"
  | "directDestination"
  | "toDestination";

export type TraditionalParcelFlowStage =
  | "selection"
  | "toPickup"
  | "arrivedPickup"
  | "toHome";

export type TraditionalCarwashFlowStage =
  | "selection"
  | "toCarwash"
  | "arrivedCarwash";

export type TraditionalRefuelFlowStage =
  | "selection"
  | "toRefuel"
  | "arrivedRefuel";

export function TraditionalDesktopMapNavigationCard({
  batteryLevel,
  collapsed,
  destinationName = "目的地",
  flowStage,
  selectedStation,
  selectedStationId,
  stations,
  target,
  onArriveCharging,
  onContinueDestination,
  onExpand,
  onGoCharge,
  onGoDestination,
  onSelectStation
}: {
  batteryLevel: number;
  collapsed: boolean;
  destinationName?: string;
  flowStage: TraditionalChargingFlowStage;
  selectedStation: ChargingStation;
  selectedStationId: string;
  stations: ChargingStation[];
  target: TraditionalDesktopDestinationTarget;
  onArriveCharging: () => void;
  onContinueDestination: () => void;
  onExpand: () => void;
  onGoCharge: () => void;
  onGoDestination: () => void;
  onSelectStation: (stationId: string) => void;
}) {
  const [chargingPriority, setChargingPriority] = useState<ChargingPriority>("distance");
  const isArrivedCharging = flowStage === "arrivedCharging";
  const isChargingNavigation = flowStage === "toCharging";
  const isDestinationNavigation = flowStage === "directDestination" || flowStage === "toDestination";
  const isChargingTarget = target === "charging" || isChargingNavigation;
  const stationName = getStationDisplayName(selectedStation);
  const routeTitle = getRouteTitle({ destinationName, isChargingTarget, stationName });
  const routeSubtitle = getRouteSubtitle({
    destinationName,
    flowStage,
    isChargingTarget
  });
  const compactSubtitle = getCompactSubtitle({
    destinationName,
    flowStage,
    selectedStation
  });
  const sortedStations = getStationsByPriority(stations, chargingPriority);
  const priorityRecommendedStation = sortedStations[0] ?? selectedStation;
  const nearestStation = getNearestStation(stations) ?? selectedStation;
  const estimatedChargeKwh = estimateChargingKwh(batteryLevel);
  const prioritySaving = getChargingSaving({
    chargeKwh: estimatedChargeKwh,
    nearestStation,
    station: priorityRecommendedStation
  });

  function handlePriorityChange(priority: ChargingPriority) {
    setChargingPriority(priority);

    const nextStation = getStationsByPriority(stations, priority)[0];

    if (nextStation && nextStation.id !== selectedStationId) {
      onSelectStation(nextStation.id);
    }
  }

  function handleCardClick() {
    if (collapsed) {
      onExpand();
    }
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!collapsed) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  }

  function handleArriveChargingClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onArriveCharging();
  }

  const maneuverSuffix = isChargingTarget || collapsed ? "m 右转" : "m 右转上主路";
  const cardRole = collapsed && !isChargingNavigation ? "button" : undefined;
  const cardTabIndex = collapsed && !isChargingNavigation ? 0 : undefined;

  if (isArrivedCharging) {
    return (
      <section
        className="traditional-desktop-map-navigation-card traditional-desktop-map-navigation-card--arrival"
        aria-label="已到达充电站"
      >
        <div className="traditional-desktop-map-navigation-card__arrival">
          <span className="traditional-desktop-map-navigation-card__arrival-icon" aria-hidden="true" />
          <div className="traditional-desktop-map-navigation-card__arrival-copy">
            <h2>已到达充电站</h2>
            <p>{stationName}，{destinationName}保持为最终目的地。</p>
          </div>
        </div>

        <div className="traditional-desktop-map-navigation-card__arrival-summary" aria-label="充电站摘要">
          <span>{selectedStation.availableStalls}个空闲桩</span>
          <span>{selectedStation.pricePerKwh.toFixed(2)}元/度</span>
          <span>{selectedStation.parkingFee}</span>
          <span>距{destinationName}{formatStationDistance(selectedStation.distanceM)}</span>
        </div>

        <div className="traditional-desktop-map-navigation-card__actions traditional-desktop-map-navigation-card__actions--arrival">
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onContinueDestination}>
            继续去原地点
          </button>
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={() => onSelectStation(selectedStation.id)}>
            切换站点
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`traditional-desktop-map-navigation-card traditional-desktop-map-navigation-card--charging${collapsed ? " traditional-desktop-map-navigation-card--collapsed" : ""}`}
      aria-label="传统桌面地图导航卡片"
      role={cardRole}
      tabIndex={cardTabIndex}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="traditional-desktop-map-navigation-card__maneuver">
        <svg className="traditional-desktop-map-navigation-card__turn-icon" viewBox="0 0 96 96" aria-hidden="true">
          <path d="M16 75V35c0-7.7 6.3-14 14-14h31V6l28 25-28 25V39H35c-2.2 0-4 1.8-4 4v32H16Z" />
        </svg>
        <span className="traditional-desktop-map-navigation-card__prefix">前方</span>
        <strong>300</strong>
        <span className="traditional-desktop-map-navigation-card__suffix">{maneuverSuffix}</span>
      </div>

      {collapsed ? (
        <div className={`traditional-desktop-map-navigation-card__compact${isChargingNavigation ? " traditional-desktop-map-navigation-card__compact--with-action" : ""}`}>
          <span className="traditional-desktop-map-navigation-card__compact-copy">
            <strong>{isChargingNavigation ? stationName : routeTitle}</strong>
            <small>{compactSubtitle}</small>
          </span>
          {isChargingNavigation ? (
            <button type="button" className="traditional-desktop-map-navigation-card__compact-action" onClick={handleArriveChargingClick}>
              到达
            </button>
          ) : null}
        </div>
      ) : null}

      {!collapsed ? (
        <>
          <span className="traditional-desktop-map-navigation-card__divider" aria-hidden="true" />

          <div className="traditional-desktop-map-navigation-card__route">
            <span>到</span>
            <strong>
              <span>{routeTitle}</span>
              <small>{routeSubtitle}</small>
            </strong>
          </div>

          <p className="traditional-desktop-map-navigation-card__meta">
            {getRouteMeta({
              destinationName,
              flowStage,
              selectedStation,
              isDestinationNavigation
            })}
          </p>

          <div className="traditional-desktop-map-navigation-card__priority" aria-label="充电站推荐策略">
            <button
              type="button"
              className={`traditional-desktop-map-navigation-card__priority-option${chargingPriority === "distance" ? " traditional-desktop-map-navigation-card__priority-option--selected" : ""}`}
              aria-pressed={chargingPriority === "distance"}
              onClick={() => handlePriorityChange("distance")}
            >
              距离优先
            </button>
            <button
              type="button"
              className={`traditional-desktop-map-navigation-card__priority-option${chargingPriority === "price" ? " traditional-desktop-map-navigation-card__priority-option--selected" : ""}`}
              aria-pressed={chargingPriority === "price"}
              onClick={() => handlePriorityChange("price")}
            >
              价格优先
            </button>
          </div>

          <div className="traditional-desktop-map-navigation-card__stations" aria-label="已筛选出的充电站">
            {sortedStations.map((station) => {
              const selected = station.id === selectedStationId;
              const recommended = station.id === priorityRecommendedStation.id;
              const saving =
                chargingPriority === "price" && recommended
                  ? prioritySaving
                  : 0;

              return (
                <button
                  key={station.id}
                  type="button"
                  className={`traditional-desktop-map-navigation-card__station${selected ? " traditional-desktop-map-navigation-card__station--selected" : ""}`}
                  onClick={() => onSelectStation(station.id)}
                >
                  <span>
                    <strong>
                      {getStationDisplayName(station)}
                      {recommended ? <em>推荐</em> : null}
                    </strong>
                    <small>
                      车程{station.routeDistanceKm.toFixed(1)}km · 距目的地{formatStationDistance(station.distanceM)} · {station.pricePerKwh.toFixed(2)}元/度 · {station.parkingFee}
                    </small>
                    <small className={saving > 0 ? "traditional-desktop-map-navigation-card__station-saving" : undefined}>
                      {station.availableStalls}个空闲桩
                      {saving > 0 ? ` · 补能约${Math.round(estimatedChargeKwh)}度省约${formatSavingAmount(saving)}元` : ""}
                    </small>
                  </span>
                  <i aria-hidden="true" />
                </button>
              );
            })}
          </div>

          <div className="traditional-desktop-map-navigation-card__actions">
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onGoCharge}>
              去充电
            </button>
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={onGoDestination}>
              继续去原地点
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

export function TraditionalDesktopCarwashNavigationCard({
  collapsed,
  destinationName = "目的地",
  destinationSearch = false,
  flowStage,
  selectedShop,
  selectedShopId,
  shops,
  onArriveCarwash,
  onExpand,
  onGoCarwash,
  onSelectShop
}: {
  collapsed: boolean;
  destinationName?: string;
  destinationSearch?: boolean;
  flowStage: TraditionalCarwashFlowStage;
  selectedShop: CarwashShop;
  selectedShopId: string;
  shops: CarwashShop[];
  onArriveCarwash: () => void;
  onExpand: () => void;
  onGoCarwash: () => void;
  onSelectShop: (shopId: string) => void;
}) {
  const isArrivedCarwash = flowStage === "arrivedCarwash";
  const isCarwashNavigation = flowStage === "toCarwash";
  const shopName = getCarwashDisplayName(selectedShop);
  const cardRole = collapsed && !isCarwashNavigation ? "button" : undefined;
  const cardTabIndex = collapsed && !isCarwashNavigation ? 0 : undefined;

  function handleCardClick() {
    if (collapsed) {
      onExpand();
    }
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!collapsed) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  }

  function handleArriveCarwashClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onArriveCarwash();
  }

  if (isArrivedCarwash) {
    return (
      <section
        className="traditional-desktop-map-navigation-card traditional-desktop-map-navigation-card--arrival traditional-desktop-map-navigation-card--carwash"
        aria-label="已到达洗车店"
      >
        <div className="traditional-desktop-map-navigation-card__arrival">
          <span className="traditional-desktop-map-navigation-card__arrival-icon traditional-desktop-map-navigation-card__arrival-icon--carwash" aria-hidden="true" />
          <div className="traditional-desktop-map-navigation-card__arrival-copy">
            <h2>已到达洗车店</h2>
            <p>{shopName}，可使用支付宝优惠完成洗车。</p>
          </div>
        </div>

        <div className="traditional-desktop-map-navigation-card__arrival-summary" aria-label="洗车店摘要">
          <span>{selectedShop.priceLabel}</span>
          <span>{selectedShop.couponLabel}</span>
          <span>等待{selectedShop.waitMinutes}分钟</span>
          <span>{selectedShop.ratingLabel}</span>
        </div>

        <div className="traditional-desktop-map-navigation-card__actions traditional-desktop-map-navigation-card__actions--arrival">
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onExpand}>
            完成
          </button>
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={() => onSelectShop(selectedShop.id)}>
            切换门店
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={[
        "traditional-desktop-map-navigation-card",
        "traditional-desktop-map-navigation-card--carwash",
        collapsed ? "traditional-desktop-map-navigation-card--collapsed" : ""
      ].filter(Boolean).join(" ")}
      aria-label="传统桌面顺路洗车导航卡片"
      role={cardRole}
      tabIndex={cardTabIndex}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="traditional-desktop-map-navigation-card__maneuver">
        <svg className="traditional-desktop-map-navigation-card__turn-icon" viewBox="0 0 96 96" aria-hidden="true">
          <path d="M16 75V35c0-7.7 6.3-14 14-14h31V6l28 25-28 25V39H35c-2.2 0-4 1.8-4 4v32H16Z" />
        </svg>
        <span className="traditional-desktop-map-navigation-card__prefix">前方</span>
        <strong>300</strong>
        <span className="traditional-desktop-map-navigation-card__suffix">m 右转</span>
      </div>

      {collapsed ? (
        <div className={`traditional-desktop-map-navigation-card__compact${isCarwashNavigation ? " traditional-desktop-map-navigation-card__compact--with-action" : ""}`}>
          <span className="traditional-desktop-map-navigation-card__compact-copy">
            <strong>{isCarwashNavigation ? shopName : destinationSearch ? `${destinationName}路线` : "顺路洗车路线"}</strong>
            <small>{destinationSearch ? `附近推荐 ${shopName} · ${selectedShop.priceLabel}` : `${selectedShop.routeMinutes}分钟 · ${selectedShop.routeDistanceKm.toFixed(1)}km · ${selectedShop.priceLabel}`}</small>
          </span>
          {isCarwashNavigation ? (
            <button type="button" className="traditional-desktop-map-navigation-card__compact-action" onClick={handleArriveCarwashClick}>
              到达
            </button>
          ) : null}
        </div>
      ) : null}

      {!collapsed ? (
        <>
          <span className="traditional-desktop-map-navigation-card__divider" aria-hidden="true" />

          <div className="traditional-desktop-map-navigation-card__route">
            <span>到</span>
            <strong>
              <span>{destinationSearch ? destinationName : shopName}</span>
              <small>{destinationSearch ? `附近推荐洗车点：${shopName}` : "已筛选顺路洗车门店"}</small>
            </strong>
          </div>

          <p className="traditional-desktop-map-navigation-card__meta">
            {destinationSearch
              ? `推荐点距${destinationName}${formatCarwashDistance(selectedShop.distanceM)} · ${selectedShop.priceLabel} · 等待${selectedShop.waitMinutes}分钟`
              : `${selectedShop.routeMinutes}分钟 · ${selectedShop.routeDistanceKm.toFixed(1)}km · 绕行${selectedShop.detourKm.toFixed(1)}km`}
          </p>

          <div className="traditional-desktop-map-navigation-card__chips">
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--carwash" aria-hidden="true" />
              <em>{selectedShop.priceLabel}</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--coupon" aria-hidden="true" />
              <em>{selectedShop.couponLabel}</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--eta" aria-hidden="true" />
              <em>等待{selectedShop.waitMinutes}分钟</em>
            </span>
          </div>

          <div className="traditional-desktop-map-navigation-card__stations" aria-label="已筛选出的洗车门店">
            {shops.map((shop) => {
              const selected = shop.id === selectedShopId;

              return (
                <button
                  key={shop.id}
                  type="button"
                  className={`traditional-desktop-map-navigation-card__station${selected ? " traditional-desktop-map-navigation-card__station--selected" : ""}`}
                  onClick={() => onSelectShop(shop.id)}
                >
                  <span>
                    <strong>
                      {getCarwashDisplayName(shop)}
                      {shop.tag ? <em>{shop.tag}</em> : null}
                    </strong>
                    <small>
                      {formatCarwashDistance(shop.distanceM)} · {shop.priceLabel} · 等待{shop.waitMinutes}分钟
                    </small>
                  </span>
                  <i aria-hidden="true" />
                </button>
              );
            })}
          </div>

          <div className="traditional-desktop-map-navigation-card__actions">
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onGoCarwash}>
              {destinationSearch ? "查看洗车点" : "去洗车"}
            </button>
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={onExpand}>
              稍后
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

export function TraditionalDesktopRefuelNavigationCard({
  collapsed,
  destinationName = "目的地",
  destinationSearch = false,
  flowStage,
  selectedStation,
  selectedStationId,
  stations,
  onArriveRefuel,
  onExpand,
  onGoRefuel,
  onSelectStation
}: {
  collapsed: boolean;
  destinationName?: string;
  destinationSearch?: boolean;
  flowStage: TraditionalRefuelFlowStage;
  selectedStation: FuelStation;
  selectedStationId: string;
  stations: FuelStation[];
  onArriveRefuel: () => void;
  onExpand: () => void;
  onGoRefuel: () => void;
  onSelectStation: (stationId: string) => void;
}) {
  const isArrivedRefuel = flowStage === "arrivedRefuel";
  const isRefuelNavigation = flowStage === "toRefuel";
  const stationName = getFuelStationDisplayName(selectedStation);
  const cardRole = collapsed && !isRefuelNavigation ? "button" : undefined;
  const cardTabIndex = collapsed && !isRefuelNavigation ? 0 : undefined;

  function handleCardClick() {
    if (collapsed) {
      onExpand();
    }
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!collapsed) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  }

  function handleArriveRefuelClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onArriveRefuel();
  }

  if (isArrivedRefuel) {
    return (
      <section
        className="traditional-desktop-map-navigation-card traditional-desktop-map-navigation-card--arrival traditional-desktop-map-navigation-card--refuel"
        aria-label="已到达加油站"
      >
        <div className="traditional-desktop-map-navigation-card__arrival">
          <span className="traditional-desktop-map-navigation-card__arrival-icon traditional-desktop-map-navigation-card__arrival-icon--refuel" aria-hidden="true" />
          <div className="traditional-desktop-map-navigation-card__arrival-copy">
            <h2>已到达加油站</h2>
            <p>{stationName}，可生成站内核销授权码。</p>
          </div>
        </div>

        <div className="traditional-desktop-map-navigation-card__arrival-summary" aria-label="加油站摘要">
          <span>{selectedStation.gradeLabel}</span>
          <span>{formatFuelPrice(selectedStation.pricePerLiter)}</span>
          <span>{selectedStation.couponLabel}</span>
          <span>排队{selectedStation.queueMinutes}分钟</span>
        </div>

        <div className="traditional-desktop-map-navigation-card__actions traditional-desktop-map-navigation-card__actions--arrival">
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onArriveRefuel}>
            生成授权码
          </button>
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={() => onSelectStation(selectedStation.id)}>
            切换站点
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={[
        "traditional-desktop-map-navigation-card",
        "traditional-desktop-map-navigation-card--refuel",
        collapsed ? "traditional-desktop-map-navigation-card--collapsed" : ""
      ].filter(Boolean).join(" ")}
      aria-label="传统桌面顺路加油导航卡片"
      role={cardRole}
      tabIndex={cardTabIndex}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="traditional-desktop-map-navigation-card__maneuver">
        <svg className="traditional-desktop-map-navigation-card__turn-icon" viewBox="0 0 96 96" aria-hidden="true">
          <path d="M16 75V35c0-7.7 6.3-14 14-14h31V6l28 25-28 25V39H35c-2.2 0-4 1.8-4 4v32H16Z" />
        </svg>
        <span className="traditional-desktop-map-navigation-card__prefix">前方</span>
        <strong>300</strong>
        <span className="traditional-desktop-map-navigation-card__suffix">m 右转</span>
      </div>

      {collapsed ? (
        <div className={`traditional-desktop-map-navigation-card__compact${isRefuelNavigation ? " traditional-desktop-map-navigation-card__compact--with-action" : ""}`}>
          <span className="traditional-desktop-map-navigation-card__compact-copy">
            <strong>{isRefuelNavigation ? stationName : destinationSearch ? `${destinationName}路线` : "顺路加油路线"}</strong>
            <small>{destinationSearch ? `附近推荐 ${stationName} · ${formatFuelPrice(selectedStation.pricePerLiter)}` : `${selectedStation.routeMinutes}分钟 · ${selectedStation.routeDistanceKm.toFixed(1)}km · ${formatFuelPrice(selectedStation.pricePerLiter)}`}</small>
          </span>
          {isRefuelNavigation ? (
            <button type="button" className="traditional-desktop-map-navigation-card__compact-action" onClick={handleArriveRefuelClick}>
              到达
            </button>
          ) : null}
        </div>
      ) : null}

      {!collapsed ? (
        <>
          <span className="traditional-desktop-map-navigation-card__divider" aria-hidden="true" />

          <div className="traditional-desktop-map-navigation-card__route">
            <span>到</span>
            <strong>
              <span>{destinationSearch ? destinationName : stationName}</span>
              <small>{destinationSearch ? `附近推荐加油站：${stationName}` : "已筛选顺路加油站"}</small>
            </strong>
          </div>

          <p className="traditional-desktop-map-navigation-card__meta">
            {destinationSearch
              ? `推荐点距${destinationName}${formatFuelDistance(selectedStation.distanceM)} · ${formatFuelPrice(selectedStation.pricePerLiter)} · 排队${selectedStation.queueMinutes}分钟`
              : `${selectedStation.routeMinutes}分钟 · ${selectedStation.routeDistanceKm.toFixed(1)}km · 绕行${selectedStation.detourKm.toFixed(1)}km`}
          </p>

          <div className="traditional-desktop-map-navigation-card__chips">
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--fuel" aria-hidden="true" />
              <em>{selectedStation.gradeLabel} · {formatFuelPrice(selectedStation.pricePerLiter)}</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--coupon" aria-hidden="true" />
              <em>{selectedStation.couponLabel}</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--eta" aria-hidden="true" />
              <em>排队{selectedStation.queueMinutes}分钟</em>
            </span>
          </div>

          <div className="traditional-desktop-map-navigation-card__stations" aria-label="已筛选出的加油站">
            {stations.map((station) => {
              const selected = station.id === selectedStationId;

              return (
                <button
                  key={station.id}
                  type="button"
                  className={`traditional-desktop-map-navigation-card__station${selected ? " traditional-desktop-map-navigation-card__station--selected" : ""}`}
                  onClick={() => onSelectStation(station.id)}
                >
                  <span>
                    <strong>
                      {getFuelStationDisplayName(station)}
                      {station.tag ? <em>{station.tag}</em> : null}
                    </strong>
                    <small>
                      {formatFuelDistance(station.distanceM)} · {station.gradeLabel} · {formatFuelPrice(station.pricePerLiter)}
                    </small>
                  </span>
                  <i aria-hidden="true" />
                </button>
              );
            })}
          </div>

          <div className="traditional-desktop-map-navigation-card__actions">
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onGoRefuel}>
              {destinationSearch ? "查看加油站" : "去加油"}
            </button>
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={onExpand}>
              稍后
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

export function TraditionalDesktopParcelNavigationCard({
  collapsed,
  flowStage,
  pendingCount,
  selectedPickupPoint,
  onArrivePickup,
  onArriveHome,
  onExpand,
  onGoHome,
  onGoPickup,
  onOpenDetail
}: {
  collapsed: boolean;
  flowStage: TraditionalParcelFlowStage;
  pendingCount: number;
  selectedPickupPoint: ParcelPickupPoint;
  onArrivePickup: () => void;
  onArriveHome: () => void;
  onExpand: () => void;
  onGoHome: () => void;
  onGoPickup: () => void;
  onOpenDetail: () => void;
}) {
  const isArrivedPickup = flowStage === "arrivedPickup";
  const isPickupNavigation = flowStage === "toPickup";
  const isHomeNavigation = flowStage === "toHome";
  const isActiveNavigation = isPickupNavigation || isHomeNavigation;
  const pointName = getPickupPointDisplayName(selectedPickupPoint);
  const activePendingCount = Math.max(1, pendingCount || selectedPickupPoint.pendingCount);
  const homeRouteMinutes = Math.max(3, selectedPickupPoint.routeMinutes);
  const cardRole = collapsed && !isActiveNavigation ? "button" : undefined;
  const cardTabIndex = collapsed && !isActiveNavigation ? 0 : undefined;

  function handleCardClick() {
    if (collapsed) {
      onExpand();
    }
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!collapsed) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  }

  function handleArriveClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (isHomeNavigation) {
      onArriveHome();
      return;
    }

    onArrivePickup();
  }

  if (isArrivedPickup) {
    return (
      <section
        className="traditional-desktop-map-navigation-card traditional-desktop-map-navigation-card--arrival traditional-desktop-map-navigation-card--parcel"
        aria-label="已到达快递驿站"
      >
        <div className="traditional-desktop-map-navigation-card__arrival">
          <span className="traditional-desktop-map-navigation-card__arrival-icon traditional-desktop-map-navigation-card__arrival-icon--parcel" aria-hidden="true" />
          <div className="traditional-desktop-map-navigation-card__arrival-copy">
            <h2>已到达取件点</h2>
            <p>{pointName}，已为你保留回家路线。</p>
          </div>
        </div>

        <div className="traditional-desktop-map-navigation-card__arrival-summary" aria-label="取件点摘要">
          <span>{activePendingCount}件待取</span>
          <span>距当前位置{formatPickupDistance(selectedPickupPoint.distanceM)}</span>
          <span>{selectedPickupPoint.eta}到达</span>
          <span>绕行{selectedPickupPoint.detourKm.toFixed(1)}km</span>
        </div>

        <div className="traditional-desktop-map-navigation-card__actions traditional-desktop-map-navigation-card__actions--arrival">
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onOpenDetail}>
            查看详情
          </button>
          <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={onGoHome}>
            继续回家
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={[
        "traditional-desktop-map-navigation-card",
        "traditional-desktop-map-navigation-card--parcel",
        collapsed ? "traditional-desktop-map-navigation-card--collapsed" : ""
      ].filter(Boolean).join(" ")}
      aria-label="传统桌面快递取件导航卡片"
      role={cardRole}
      tabIndex={cardTabIndex}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="traditional-desktop-map-navigation-card__maneuver">
        <svg className="traditional-desktop-map-navigation-card__turn-icon" viewBox="0 0 96 96" aria-hidden="true">
          <path d="M16 75V35c0-7.7 6.3-14 14-14h31V6l28 25-28 25V39H35c-2.2 0-4 1.8-4 4v32H16Z" />
        </svg>
        <span className="traditional-desktop-map-navigation-card__prefix">前方</span>
        <strong>300</strong>
        <span className="traditional-desktop-map-navigation-card__suffix">m 右转</span>
      </div>

      {collapsed ? (
        <div className={`traditional-desktop-map-navigation-card__compact${isActiveNavigation ? " traditional-desktop-map-navigation-card__compact--with-action" : ""}`}>
          <span className="traditional-desktop-map-navigation-card__compact-copy">
            <strong>{isHomeNavigation ? "回家路线" : isPickupNavigation ? pointName : "快递取件路线"}</strong>
            <small>
              {isHomeNavigation
                ? `${homeRouteMinutes}分钟 · ${activePendingCount}件驿站待取`
                : `${selectedPickupPoint.routeMinutes}分钟 · ${selectedPickupPoint.routeDistanceKm.toFixed(1)}km · ${activePendingCount}件待取`}
            </small>
          </span>
          {isActiveNavigation ? (
            <button type="button" className="traditional-desktop-map-navigation-card__compact-action" onClick={handleArriveClick}>
              {isHomeNavigation ? "到家" : "到达"}
            </button>
          ) : null}
        </div>
      ) : null}

      {!collapsed ? (
        <>
          <span className="traditional-desktop-map-navigation-card__divider" aria-hidden="true" />

          <div className="traditional-desktop-map-navigation-card__route">
            <span>到</span>
            <strong>
              <span>{pointName}</span>
              <small>家附近已绑定取件点</small>
            </strong>
          </div>

          <p className="traditional-desktop-map-navigation-card__meta">
            {selectedPickupPoint.routeMinutes}分钟 · {selectedPickupPoint.routeDistanceKm.toFixed(1)}km · 绕行{selectedPickupPoint.detourKm.toFixed(1)}km
          </p>

          <div className="traditional-desktop-map-navigation-card__chips">
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--parcel" aria-hidden="true" />
              <em>{activePendingCount}件待取</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--eta" aria-hidden="true" />
              <em>{selectedPickupPoint.eta}到达</em>
            </span>
            <span className="traditional-desktop-map-navigation-card__chip">
              <i className="traditional-desktop-map-navigation-card__chip-icon traditional-desktop-map-navigation-card__chip-icon--distance" aria-hidden="true" />
              <em>{formatPickupDistance(selectedPickupPoint.distanceM)}</em>
            </span>
          </div>

          <div className="traditional-desktop-map-navigation-card__parcel-stop" aria-label="当前快递绑定取件点">
            <span className="traditional-desktop-map-navigation-card__parcel-stop-icon" aria-hidden="true" />
            <span>
              <strong>{pointName}</strong>
              <small>{selectedPickupPoint.address} · 已绑定{activePendingCount}件待取包裹</small>
            </span>
          </div>

          <div className="traditional-desktop-map-navigation-card__actions">
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--primary" onClick={onGoPickup}>
              导航去取件
            </button>
            <button type="button" className="traditional-desktop-map-navigation-card__action traditional-desktop-map-navigation-card__action--secondary" onClick={onOpenDetail}>
              查看详情
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}

type ChargingPriority = "distance" | "price";

function getStationsByPriority(stations: ChargingStation[], priority: ChargingPriority) {
  if (priority === "price") {
    return [...stations].sort(
      (a, b) =>
        a.pricePerKwh - b.pricePerKwh ||
        a.distanceM - b.distanceM ||
        b.availableStalls - a.availableStalls
    );
  }

  return [...stations].sort(
    (a, b) =>
      a.distanceM - b.distanceM ||
      a.pricePerKwh - b.pricePerKwh ||
      b.availableStalls - a.availableStalls
  );
}

function getNearestStation(stations: ChargingStation[]) {
  return getStationsByPriority(stations, "distance")[0];
}

function estimateChargingKwh(batteryLevel: number) {
  const currentBatteryLevel = Math.min(100, Math.max(5, Math.round(batteryLevel)));
  const targetBatteryLevel =
    currentBatteryLevel >= 90
      ? currentBatteryLevel
      : Math.max(TARGET_BATTERY_LEVEL, Math.min(90, currentBatteryLevel + 24));
  return USABLE_BATTERY_KWH * Math.max(0, targetBatteryLevel - currentBatteryLevel) / 100;
}

function getChargingSaving({
  chargeKwh,
  nearestStation,
  station
}: {
  chargeKwh: number;
  nearestStation: ChargingStation;
  station: ChargingStation;
}) {
  return Math.max(0, (nearestStation.pricePerKwh - station.pricePerKwh) * chargeKwh);
}

function formatSavingAmount(value: number) {
  return value >= 10 ? Math.round(value).toString() : value.toFixed(1);
}

function getRouteTitle({
  destinationName,
  isChargingTarget,
  stationName
}: {
  destinationName: string;
  isChargingTarget: boolean;
  stationName: string;
}) {
  return isChargingTarget ? stationName : destinationName;
}

function getRouteSubtitle({
  destinationName,
  flowStage,
  isChargingTarget
}: {
  destinationName: string;
  flowStage: TraditionalChargingFlowStage;
  isChargingTarget: boolean;
}) {
  if (flowStage === "toDestination") {
    return "已从充电站继续出发";
  }

  if (flowStage === "directDestination") {
    return "到达后剩余电量偏低";
  }

  return isChargingTarget ? `${destinationName}保持为最终目的地` : "附近已筛选快充站";
}

function getCompactSubtitle({
  destinationName,
  flowStage,
  selectedStation
}: {
  destinationName: string;
  flowStage: TraditionalChargingFlowStage;
  selectedStation: ChargingStation;
}) {
  if (flowStage === "toCharging") {
    return `${selectedStation.routeMinutes}分钟 · ${selectedStation.routeDistanceKm.toFixed(1)}km · ${destinationName}保持为最终目的地`;
  }

  if (flowStage === "toDestination") {
    return `从充电站继续出发 · 距${destinationName}${formatStationDistance(selectedStation.distanceM)}`;
  }

  if (flowStage === "directDestination") {
    return `直接前往${destinationName} · 到达后剩余电量偏低`;
  }

  return `${selectedStation.availableStalls}个空闲桩 · ${selectedStation.pricePerKwh.toFixed(2)}元/度`;
}

function getRouteMeta({
  destinationName,
  flowStage,
  selectedStation,
  isDestinationNavigation
}: {
  destinationName: string;
  flowStage: TraditionalChargingFlowStage;
  selectedStation: ChargingStation;
  isDestinationNavigation: boolean;
}) {
  if (flowStage === "directDestination") {
    return "12分钟 · 到达后剩余电量偏低";
  }

  if (flowStage === "toDestination") {
    return `约12分钟 · 充电站距${destinationName}${formatStationDistance(selectedStation.distanceM)}`;
  }

  if (isDestinationNavigation) {
    return `12分钟 · ${destinationName}附近保留补能推荐`;
  }

  return `${selectedStation.routeMinutes}分钟 · ${selectedStation.routeDistanceKm.toFixed(1)}km · 充电站距${destinationName}${formatStationDistance(selectedStation.distanceM)}`;
}
